"use client";
import { useQuery } from '@tanstack/react-query';
import PleaseWait from '@/components/loader/PleaseWait';
import { getProgram } from '@/lib/client/movieinfo';
import { type ICurrentMoviesProgram, type IShowsForMovie } from '@/lib/types/Program';
import type UserSettings from '@/lib/types/UserSettings';
import type { ReactNode } from 'react';
import React from 'react';
import styles from "./DataLoader.module.css";


//#region [Props]
type DataLoaderProps = {
    children: ReactNode;
    date?: string;
    loaderText: string;
    edi?: string;
    skipDocuments?: boolean;
    className?: string;
    userSettings?: UserSettings;
    isPremiereInFuture?: boolean;
};
//#endregion

//#region [Component]
export default function DataLoader({ edi, date, skipDocuments = false, children, loaderText, className, userSettings, isPremiereInFuture }: DataLoaderProps) {
    const { data, isFetching, isLoading } = useQuery({
        queryKey: ["program-section", date ?? "all-dates", edi ?? "all-movies", skipDocuments],
        queryFn: () => getProgram(edi, date, !skipDocuments, undefined, !edi), // loader function
        refetchInterval: 600000, // refretch automatically every 10 minutes
        staleTime: 599999,
    }
    );

    return (<div aria-busy={isFetching || isLoading} className={className}>
        {isLoading && <PleaseWait text={loaderText} />}
        {!isLoading && React.Children.map(children, child => {
            // Checking isValidElement is the safe way and avoids a typescript
            // error too.
            if (React.isValidElement(child)) {
                return React.cloneElement(child as React.ReactElement<Record<string, unknown>>, { data });
            }
            return child;
        })}
        {!isLoading && <>
            {!edi && ((data as ICurrentMoviesProgram)?.movies?.length ?? 0) === 0 && <p className={styles.SNoShows}>Ingen forestillinger {date && <>for valgt dag</>}</p>}
            {edi && ((data as IShowsForMovie)?.shows?.length ?? 0) === 0 && <>
                {isPremiereInFuture && <p className={styles.SNoShows}>{userSettings?.noShowsFuture?.trim() ? userSettings!.noShowsFuture : "Ingen forestillinger er i salg ennå."}</p>}
                {!isPremiereInFuture && <p className={styles.SNoShows}>{userSettings?.noShows?.trim() ? userSettings!.noShows : "Ingen forestillinger er i salg."}</p>}

            </>}
        </>}
        {/** TODO: Show smaller loader when fetching */}
    </div>);
}
//#endregion